/**
 * Right-click-to-cancel for the picking/editing tools, without eating
 * right-drag camera pans.
 *
 * The naive approach — cancelling inside a `contextmenu` listener — breaks
 * as soon as the user right-drags to pan (the default orbit-control
 * binding in the orthographic view): the browser fires `contextmenu` on
 * the right button regardless of how far it travelled. Worse, on macOS
 * `contextmenu` fires on the *down*, before any movement has happened,
 * so a distance check inside that handler can never work.
 *
 * So, like `SceneContextMenuPicker`, the decision is made on the right
 * button's `pointerup`: record where the button went down and only cancel
 * when it comes up within `RIGHT_CLICK_DRAG_THRESHOLD_PX`. `contextmenu`
 * is kept purely to suppress the native menu.
 *
 * `pointerup` is listened on `window` so a release that lands just outside
 * the canvas still resolves the gesture (and a real pan that ends outside
 * still clears the recorded down position instead of leaking into the
 * next click).
 */
export const RIGHT_CLICK_DRAG_THRESHOLD_PX = 5;

export function attachRightClickCancel(
  el: HTMLElement,
  onCancel: () => void,
  threshold = RIGHT_CLICK_DRAG_THRESHOLD_PX,
): () => void {
  let downPos: { x: number; y: number } | null = null;

  const onDown = (e: PointerEvent) => {
    if (e.button !== 2) return;
    downPos = { x: e.clientX, y: e.clientY };
  };

  const onUp = (e: PointerEvent) => {
    if (e.button !== 2 || !downPos) return;
    const dx = e.clientX - downPos.x;
    const dy = e.clientY - downPos.y;
    downPos = null;
    if (Math.hypot(dx, dy) >= threshold) return;
    onCancel();
  };

  const onCtx = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  el.addEventListener('pointerdown', onDown);
  window.addEventListener('pointerup', onUp);
  el.addEventListener('contextmenu', onCtx);
  return () => {
    el.removeEventListener('pointerdown', onDown);
    window.removeEventListener('pointerup', onUp);
    el.removeEventListener('contextmenu', onCtx);
  };
}
